import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import { formatBRL } from '@/pages/painel/lib/radarConstants';

interface PrioridadesHojeProps {
  password: string;
  refreshKey: number;
}

interface Acao {
  id: string;
  source: string;
  entity_id: number;
  acao: string;
  origem: 'radar' | 'obra' | 'crm';
  origem_label: string;
  empresa: string | null;
  empresa_id: number | null;
  oportunidade: string | null;
  oportunidade_id: number | null;
  radar_id: number | null;
  papel: string | null;
  data: string | null;
  dias: number | null;
  vencida: boolean;
  hoje: boolean;
  valor_potencial: number | null;
  lucro_potencial: number | null;
  hop_score: number | null;
  etapa: string | null;
  prioridade: number;
  nivel: string;
  motivos: string[];
  contexto_url: string;
}

interface SemAcao {
  id: number;
  origem: string;
  origem_label: string;
  titulo: string | null;
  empresa: string | null;
  cidade: string | null;
  valor_potencial: number | null;
  lucro_potencial: number | null;
  hop_score: number | null;
  etapa: string | null;
  contexto_url: string;
  quente: boolean;
}

interface Kpis {
  acoes_hoje: number;
  acoes_atrasadas: number;
  quentes_sem_acao: number;
  valor_prioridades: number;
  lucro_prioridades: number;
}

interface PrioridadesData {
  acoes: Acao[];
  sem_acao: SemAcao[];
  kpis: Kpis;
}

const NIVEL_TONE: Record<string, { label: string; icon: string; tone: string }> = {
  agir_agora: { label: 'Agir agora', icon: 'ri-fire-fill', tone: 'bg-red-50 text-red-600 border-red-200' },
  prioridade: { label: 'Prioridade', icon: 'ri-arrow-up-circle-fill', tone: 'bg-green-50 text-green-700 border-green-200' },
  monitorar: { label: 'Monitorar', icon: 'ri-time-fill', tone: 'bg-amber-50 text-amber-700 border-amber-200' },
  baixa: { label: 'Baixa prioridade', icon: 'ri-record-circle-line', tone: 'bg-gray-100 text-gray-500 border-gray-200' },
};

const ORIGEM_TONE: Record<string, string> = {
  radar: 'bg-orange-50 text-orange-700 border-orange-200',
  obra: 'bg-amber-50 text-amber-700 border-amber-200',
  crm: 'bg-indigo-50 text-indigo-700 border-indigo-200',
};

function formatData(data: string | null, dias: number | null, vencida: boolean, hoje: boolean): string {
  if (!data) return 'Sem data';
  if (hoje) return 'Hoje';
  if (vencida) {
    const n = Math.abs(dias ?? 0);
    return `Vencida há ${n} ${n === 1 ? 'dia' : 'dias'}`;
  }
  if (dias != null && dias === 1) return 'Amanhã';
  const d = new Date(data + 'T00:00:00Z');
  if (Number.isNaN(d.getTime())) return 'Sem data';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export default function PrioridadesHoje({ password, refreshKey }: PrioridadesHojeProps) {
  const [data, setData] = useState<PrioridadesData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [completing, setCompleting] = useState<string | null>(null);
  const [concluidoMsg, setConcluidoMsg] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: resp, error: invokeErr } = await supabase.functions.invoke('crm-prioridades', {
        body: { action: 'listar', password },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = resp as PrioridadesData & { error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setData(parsed);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar prioridades.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const concluir = async (a: Acao) => {
    setCompleting(a.id);
    setConcluidoMsg(null);
    try {
      const { data: resp, error: invokeErr } = await supabase.functions.invoke('crm-prioridades', {
        body: { action: 'concluir', password, source: a.source, entity_id: a.entity_id },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = resp as { ok?: boolean; error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      setConcluidoMsg(`Ação concluída: "${a.acao}". Deseja definir a próxima ação?`);
      await load();
    } catch {
      // silencioso — mantém estado
    } finally {
      setCompleting(null);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#E8610A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-red-600 mb-3">{error}</p>
        <button
          onClick={load}
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#E8610A] hover:text-[#C4500A] cursor-pointer"
        >
          <i className="ri-refresh-line"></i>
          Tentar novamente
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { acoes, sem_acao: semAcao, kpis } = data;

  const agirHoje = acoes.filter((a) => a.vencida || a.hoje);
  const proximos = acoes.filter((a) => !a.vencida && !a.hoje && ((a.dias != null && a.dias <= 7) || (a.dias == null && a.prioridade >= 50)));

  const listaPrioritaria = [...agirHoje, ...proximos];
  const visiveis = showAll ? listaPrioritaria : listaPrioritaria.slice(0, 7);

  const kpiCards = [
    { label: 'Ações para hoje', value: kpis.acoes_hoje, icon: 'ri-sun-line', tone: 'bg-amber-50 text-amber-600' },
    { label: 'Ações atrasadas', value: kpis.acoes_atrasadas, icon: 'ri-alarm-warning-line', tone: 'bg-red-50 text-red-600' },
    { label: 'Quentes sem próxima ação', value: kpis.quentes_sem_acao, icon: 'ri-fire-line', tone: 'bg-orange-50 text-orange-600' },
    { label: 'Valor potencial (prioridades)', value: formatBRL(kpis.valor_prioridades), icon: 'ri-coins-line', tone: 'bg-green-50 text-green-700' },
    { label: 'Lucro potencial (prioridades)', value: formatBRL(kpis.lucro_prioridades), icon: 'ri-funds-line', tone: 'bg-emerald-50 text-emerald-700' },
  ];

  const renderAcao = (a: Acao) => {
    const nivel = NIVEL_TONE[a.nivel] ?? NIVEL_TONE.baixa;
    const origem = ORIGEM_TONE[a.origem] ?? ORIGEM_TONE.crm;
    return (
      <div
        key={a.id}
        className={`rounded-xl border p-4 bg-white ${a.vencida ? 'border-red-200 bg-red-50/40' : 'border-hop-border'}`}
      >
        <div className="flex items-start gap-3">
          <div className={`w-9 h-9 shrink-0 flex items-center justify-center rounded-lg ${nivel.tone}`}>
            <i className={nivel.icon}></i>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-semibold text-gray-900 leading-snug">{a.acao}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap mt-1.5">
              {a.empresa && (
                <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                  <i className="ri-building-4-line"></i>
                  {a.empresa}
                </span>
              )}
              {a.oportunidade && (
                <span className="inline-flex items-center gap-1 text-xs text-gray-500 truncate max-w-[220px]">
                  <i className="ri-briefcase-line"></i>
                  {a.oportunidade}
                </span>
              )}
              <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border ${origem}`}>
                {a.origem_label}
              </span>
              {a.papel && (
                <span className="inline-flex items-center text-[11px] text-gray-500 px-2 py-0.5 rounded-full bg-gray-100">
                  {a.papel}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap mt-2">
              <span className={`inline-flex items-center gap-1 text-xs font-semibold whitespace-nowrap ${a.vencida ? 'text-red-600' : a.hoje ? 'text-amber-600' : 'text-gray-500'}`}>
                <i className={a.vencida ? 'ri-alarm-warning-line' : 'ri-calendar-line'}></i>
                {formatData(a.data, a.dias, a.vencida, a.hoje)}
              </span>
              {a.valor_potencial != null && a.valor_potencial > 0 && (
                <span className="text-xs font-semibold text-gray-700">
                  Potencial {formatBRL(a.valor_potencial)}
                </span>
              )}
              {a.lucro_potencial != null && a.lucro_potencial > 0 && (
                <span className="text-xs font-semibold text-green-700">
                  Lucro {formatBRL(a.lucro_potencial)}
                </span>
              )}
              {a.hop_score != null && (
                <span className="inline-flex items-center gap-1 text-xs font-bold text-orange-700 bg-orange-50 rounded-full px-2 py-0.5">
                  <i className="ri-radar-line"></i>
                  {a.hop_score}
                </span>
              )}
              <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full border ${nivel.tone}`} title={a.motivos.join(' · ')}>
                <i className={nivel.icon}></i>
                {nivel.label} · {a.prioridade}
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1.5 shrink-0">
            <Link
              to={a.contexto_url}
              className="inline-flex items-center gap-1 text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] px-2.5 py-1.5 rounded-lg hover:bg-[#E8610A]/10 transition-colors whitespace-nowrap cursor-pointer"
            >
              Abrir contexto
              <i className="ri-arrow-right-line"></i>
            </Link>
            <button
              onClick={() => concluir(a)}
              disabled={completing === a.id}
              className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg px-2.5 py-1.5 cursor-pointer whitespace-nowrap disabled:opacity-60"
            >
              {completing === a.id ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i className="ri-check-line"></i>
              )}
              Concluir
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSemAcao = (s: SemAcao) => (
    <div key={`${s.origem}-${s.id}`} className={`rounded-xl border p-3.5 bg-white ${s.quente ? 'border-orange-200 bg-orange-50/30' : 'border-hop-border'}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 shrink-0 flex items-center justify-center rounded-lg ${s.quente ? 'bg-orange-50 text-orange-600' : 'bg-gray-50 text-gray-500'}`}>
          <i className={s.quente ? 'ri-fire-line' : 'ri-more-line'}></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 leading-snug">{s.titulo ?? s.empresa ?? 'Oportunidade'}</p>
          <div className="flex items-center gap-2 flex-wrap mt-1.5">
            {s.empresa && <span className="text-xs text-gray-600">{s.empresa}</span>}
            {s.cidade && <span className="text-xs text-gray-400">{s.cidade}</span>}
            <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full border ${ORIGEM_TONE[s.origem] ?? ORIGEM_TONE.crm}`}>
              {s.origem_label}
            </span>
          </div>
          <div className="flex items-center gap-3 flex-wrap mt-1.5">
            {s.valor_potencial != null && s.valor_potencial > 0 && (
              <span className="text-xs font-semibold text-gray-700">{formatBRL(s.valor_potencial)}</span>
            )}
            {s.hop_score != null && (
              <span className="inline-flex items-center gap-1 text-xs font-bold text-orange-700 bg-orange-50 rounded-full px-2 py-0.5">
                <i className="ri-radar-line"></i>
                {s.hop_score}
              </span>
            )}
            <span className="text-[11px] font-semibold text-red-600">Sem próxima ação</span>
          </div>
        </div>
        <Link
          to={s.contexto_url}
          className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-[#E8610A] hover:text-[#C4500A] px-2.5 py-1.5 rounded-lg hover:bg-[#E8610A]/10 transition-colors whitespace-nowrap cursor-pointer"
        >
          Definir ação
          <i className="ri-arrow-right-line"></i>
        </Link>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Minhas prioridades de hoje</h2>
          <p className="text-sm text-gray-500 mt-1">
            Ações consolidadas do Radar, relacionamentos e CRM, priorizadas por resultado comercial.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 whitespace-nowrap cursor-pointer disabled:opacity-60"
        >
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          Atualizar
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
      )}

      {concluidoMsg && (
        <div className="flex items-center justify-between gap-3 text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
          <span>{concluidoMsg}</span>
          <Link
            to={listaPrioritaria[0]?.contexto_url ?? '/painel/radar'}
            className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 hover:underline cursor-pointer whitespace-nowrap"
          >
            Definir próxima ação
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      )}

      {/* Indicadores de inteligência operacional */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {kpiCards.map((c) => (
          <div key={c.label} className="bg-white rounded-xl border border-hop-border p-4">
            <div className="flex items-center gap-2.5">
              <div className={`w-9 h-9 flex items-center justify-center rounded-lg ${c.tone}`}>
                <i className={c.icon}></i>
              </div>
              <div className="min-w-0">
                <p className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold leading-tight">{c.label}</p>
                <p className="text-base font-bold text-gray-900 truncate">{c.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* AGIR HOJE */}
      <section className="bg-white rounded-2xl border border-hop-border p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-600">
            <i className="ri-fire-fill"></i>
          </div>
          <h3 className="font-bold text-gray-900">Agir hoje</h3>
          <span className="text-xs font-semibold text-red-600 bg-red-50 rounded-full px-2 py-0.5">{agirHoje.length}</span>
        </div>
        {agirHoje.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">Nenhuma ação vencida ou para hoje.</p>
        ) : (
          <div className="space-y-2">{agirHoje.map(renderAcao)}</div>
        )}
      </section>

      {/* PRÓXIMOS 7 DIAS */}
      <section className="bg-white rounded-2xl border border-hop-border p-5">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-50 text-amber-600">
            <i className="ri-calendar-check-line"></i>
          </div>
          <h3 className="font-bold text-gray-900">Próximos 7 dias</h3>
          <span className="text-xs font-semibold text-gray-400 bg-hop-bg-alt rounded-full px-2 py-0.5">{proximos.length}</span>
        </div>
        {proximos.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">Nada agendado para os próximos dias.</p>
        ) : (
          <div className="space-y-2">
            {visiveis
              .filter((a) => !a.vencida && !a.hoje)
              .map(renderAcao)}
          </div>
        )}
      </section>

      {listaPrioritaria.length > 7 && (
        <button
          onClick={() => setShowAll((s) => !s)}
          className="w-full inline-flex items-center justify-center gap-2 text-sm font-semibold text-[#E8610A] hover:text-[#C4500A] py-2 cursor-pointer whitespace-nowrap"
        >
          {showAll ? (
            <>
              <i className="ri-arrow-up-s-line"></i>
              Mostrar menos
            </>
          ) : (
            <>
              Mostrar todas as {listaPrioritaria.length} ações
              <i className="ri-arrow-down-s-line"></i>
            </>
          )}
        </button>
      )}

      {/* SEM PRÓXIMA AÇÃO */}
      <section className="bg-white rounded-2xl border-2 border-orange-200 p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-50 text-orange-600">
            <i className="ri-alert-line"></i>
          </div>
          <h3 className="font-bold text-gray-900">Sem próxima ação</h3>
          <span className="text-xs font-semibold text-orange-700 bg-orange-50 rounded-full px-2 py-0.5">{semAcao.length}</span>
        </div>
        <p className="text-xs text-gray-500 mb-3">
          Oportunidades relevantes que ainda não têm o próximo passo definido.
        </p>
        {semAcao.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">Todas as oportunidades relevantes têm ação definida.</p>
        ) : (
          <div className="space-y-2">{semAcao.slice(0, 10).map(renderSemAcao)}</div>
        )}
      </section>
    </div>
  );
}